import Button from '@/components/base/button/button';
import Divider from '@/components/base/divider/divider';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import InformationSection from '@/features/navigation/components/information-section/information-section';
import { LanguageChange } from '@/features/navigation/components/information-section/language-change';
import { MobileNavigationLink } from '@/features/navigation/components/mobile-menu/mobile-navigation-link';
import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import type { Navigation } from '@/features/navigation/types/navigation';
import { useRouter } from '@/navigation';
import { RESERVATION_ROUTES } from '@/routes';
import { Redirect, getRedirectPath } from '@/utils/redirects';
import { XCircle } from '@phosphor-icons/react';
import { useKeyboardEvent, useMeasure } from '@react-hookz/web';
import { clsx } from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect } from 'react';
import FocusLock, { useFocusScope } from 'react-focus-lock';

interface Props {
  data: Navigation[];
  closeMenu: () => void;
}

const selector = ({
  isMobileMenuOpen,
  setMobileMenuTopSectionHeight,
  setNavigationPath,
  navigationPath,
}: NavigationStore) => ({
  isMobileMenuOpen,
  setMobileMenuTopSectionHeight,
  setNavigationPath,
  navigationPath,
});

export const MenuWrapper = ({ data, closeMenu }: Props) => {
  const t = useTranslations('common');
  const tn = useTranslations('navigation');
  const {
    isMobileMenuOpen: isOpen,
    setMobileMenuTopSectionHeight,
    setNavigationPath,
    navigationPath,
  } = useNavigationStore(selector);
  const { push } = useRouter();

  const locale = useLocale();
  const [size, mainTopSectionRef] = useMeasure<HTMLDivElement>();

  const { focusNext, focusPrev } = useFocusScope();

  const onLinkClick = (id: number) => {
    setNavigationPath(id);
  };

  useKeyboardEvent('ArrowDown', (e) => {
    if (!isOpen) return;
    e.preventDefault();
    focusNext();
  });

  useKeyboardEvent('ArrowUp', (e) => {
    if (!isOpen) return;

    e.preventDefault();
    focusPrev();
  });

  const redirectToVouchers = () => {
    closeMenu();
    push(RESERVATION_ROUTES.RESERVATION);
  };

  useEffect(() => {
    setMobileMenuTopSectionHeight(size?.height);
  }, [size, setMobileMenuTopSectionHeight]);

  return (
    <>
      <FocusLock
        as="div"
        disabled={!isOpen}
        className={clsx(
          'absolute z-50 flex w-full flex-col s:justify-between bg-main px-xs s:px-[30px] xs:px-s s:pt-s s:pr-xs pb-3',
          navigationPath.length > 0 ? 'gap-0 pl-0 s:pl-xs' : 'gap-xs',
        )}
        group="mobile-menu"
        autoFocus={false}
        ref={mainTopSectionRef}
      >
        <div className="flex items-center justify-between">
          <LanguageChange />
          <Button
            variant="tertiary-no-frame"
            className="!-mr-xs s:!mr-0 h-clickable s:h-clickable-large s:w-clickable-large w-clickable justify-self-end only:mr-0 only:ml-auto"
            aria-label={t('close')}
            onClick={closeMenu}
            testId={'mobile-menu-close-button'}
          >
            <PhosphorIcon
              Icon={XCircle}
              variant="regular"
              className="icon-m s:icon-l"
            />
          </Button>
        </div>
      </FocusLock>

      <div
        className={clsx('s:mt-s flex h-auto flex-1 flex-col')}
        style={{
          paddingTop: `${(size?.height ?? 48) + 6 + 3}px`,
        }}
        data-testid={'mobile_menu_wrapper'}
      >
        <div className={clsx('w-full px-xs s:px-[30px] max-s:shrink-0')}>
          <Button
            variant="tertiary"
            className="s:w-max w-full"
            onClick={redirectToVouchers}
            testId={'redirect_to_vouchers'}
          >
            {tn('main_section.i_have_a_voucher')}
          </Button>
        </div>
        <ul
          className={clsx(
            'mt-xxs flex h-max w-full shrink-0 flex-col items-center justify-start overflow-hidden s:px-s xs:px-xxs pt-m pb-l',
            navigationPath.length > 0 && 'mt-xxs',
          )}
          hidden={!isOpen}
          id="mobile-menu"
        >
          {data.map(({ title, id, children, href }) => (
            <MobileNavigationLink
              title={title}
              key={id}
              href={href}
              id={id}
              onClick={() => onLinkClick(id)}
              redirectToEntireCategory={false}
              nodes={children}
            />
          ))}
          <div className="w-full px-xs py-xs">
            <Divider />
          </div>
          <MobileNavigationLink
            title={tn('btn_contact')}
            href={getRedirectPath(Redirect.HELP, locale)}
            id={0}
          />
        </ul>
        <InformationSection className="m:hidden" />
      </div>
    </>
  );
};
